/**
 * normalizeTrip — converts the REST current-trip record (and, via the reducer,
 * a trip-location-update delta) into one unified in-memory record.
 *
 * The record intentionally keeps the legacy `metadata.*` + flat-dynamic shape
 * that the existing dashboard components (Map.js, filterUtils.js, SidePanel.js,
 * BottomPanel.js) already read, so those stay behavior-preserving. The REST
 * `staticFlags` and the socket `liveFlags` are mapped into `metadata.tripFlags`
 * / `metadata.delayFlags` / flat fields accordingly.
 *
 * Data sources (see specs/022-live-tracking-stability/contracts/):
 *  - static metadata  -> REST Trip Record (preserved across merges)
 *  - dynamic fields   -> REST `live` block, then overwritten by trip-location-update
 */

// Derived fields used by the memoized marker (Map.js). Recomputed after each
// merge. Female icon is driven solely by staticFlags (FR-025a / clarification Q5).
export const deriveTripFields = (record) => {
  const flags = record?.metadata?.tripFlags || {};
  const vehicleType = String(record?.metadata?.vehicleInfo?.vehicleType || '').toUpperCase();
  // Shuttle trips use the bus marker even if the assigned vehicle isn't a BUS type.
  const isShuttle = record?.metadata?.tripInfo?.tripCategory === 'SHUTTLE';
  const isBus = vehicleType.includes('BUS') || isShuttle;
  const isFemale = flags.isFemaleTraveling === true || flags.isFemaleTraveling1 === true;
  const isCompleted = String(record?.tripStatus || '').toUpperCase() === 'COMPLETED';

  let iconStatus = 'default';
  if (isCompleted) iconStatus = 'completed';
  else if (isFemale) iconStatus = 'female';

  return { ...record, _isBus: isBus, _isFemale: isFemale, _iconStatus: iconStatus };
};

export const normalizeTrip = (rest) => {
  if (!rest || !rest.tripId) return null;
  const live = rest.live || {};
  const sf = rest.staticFlags || {};

  const record = {
    tripId: rest.tripId,
    corporateId: rest.corporateId,
    vendorId: rest.vendorId,
    driverId: rest.driverId || null, // REST omits driverId; arrives on trip-location-update

    // flat dynamic fields (components read these directly)
    latitude: live.latitude,
    longitude: live.longitude,
    heading: live.heading || 0,
    speed: live.speed,
    time: live.time,
    tripStatus: live.tripStatus || rest?.tripInfo?.tripStatus,
    onboardCount: live.onboardCount,
    vehicleNearOffice: live.vehicleNearOffice === true,
    isOverSpeeding: live.isOverSpeeding === true,
    safetyCheckRequired: live.safetyCheckRequired === true,
    geoFenceViolation: live.geoFenceViolation === true, // usually arrives via liveFlags on trip-location-update
    GPS_SIGNAL_LOST: live.GPS_SIGNAL_LOST === true,
    isServerGenerated: live.isServerGenerated === true,
    scheduledStopsETA: live.scheduledStopsETA || [],
    finalDestinationETA: live.finalDestinationETA,
    passengerETA: live.passengerETA || {},
    companyLogo: rest.companyLogo,
    // Shuttle: seat capacity (static, from the assigned vehicle) + currently-boarded (from the
    // ShuttleBoarding log, refreshed on each snapshot). See S8.
    capacity: rest.vehicleInfo?.capacity ?? null,
    boardedCount: rest.tripInfo?.boardedCount ?? 0,

    metadata: {
      tripInfo: rest.tripInfo || {},
      driverInfo: rest.driverInfo || {},
      vehicleInfo: rest.vehicleInfo || {},
      passengersInfo: rest.passengersInfo || {},
      vendorInfo: rest.vendorInfo || {},
      escortInfo: rest.escortInfo || {},
      tripFlags: {
        // static (REST staticFlags)
        isFemaleTraveling: sf.isFemaleTraveling === true,
        isFemaleTraveling1: sf.isFemaleTraveling1 === true,
        isSpecialEmployeeTrip: sf.isSpecialEmployeeTrip === true,
        isAdhoc: sf.isAdhoc === true,
        isShuttle: sf.isShuttle === true,
        // dynamic (liveFlags) — false until first trip-location-update
        isSOS: false,
        hasStoppedOver5Mins: false,
        isMaxTravelTimeViolation: false,
      },
      delayFlags: {},
    },

    // bookkeeping
    _lastTime: live.time || 0,
    _lastUpdatedAt: Date.now(),
    _stale: false,
  };

  return deriveTripFields(record);
};
